import tempMee from '../apis/tempMee';
import { REFERRAL_ACTIONS, SHOW_MESSAGE } from './actionTypes';
import { mapReferrals } from '../domain/Referrals/mappers';
import { getShouldSourceReferralsFromReferralService } from '../posthog';

export const REFERRAL_LIST_ENDPOINT = '/referral-service/admin/referrals';
export const REFERRAL_LIST_ENDPOINT_MONOLITH = 'admin/referral';

// ReferralList re-fetches both on a filter change and on the PostHog flag
// resolving — those two triggers can race (the flag often resolves after the
// list has already mounted and fired the flag-off fetch). Whichever response
// lands LAST used to win regardless of which request was newest, so a slow
// monolith reply could overwrite a fresher referral-service one (or vice
// versa) after it had already rendered. A module-level sequence number,
// bumped per `fetchReferrals` call and captured in each dispatched action,
// lets the reducer/UI tell which response is actually the latest and drop
// anything older instead of blindly applying whatever arrives last.
let requestSequence = 0;

// Both selects send their 'all' sentinel through verbatim; referral-service
// (like the monolith before it) treats an omitted filter as "any", so 'all'
// is dropped rather than forwarded.
const REFERRER_TYPE_TO_PARAM = {
  HYG: 'professional',
  DNT: 'office',
};

/**
 * Fetches the Referral list from whichever backend the
 * `referralProgramRebuild` flag selects — referral-service's admin read
 * endpoint when on, the monolith's `admin/referral` when off (GOTU-9287
 * gated behind GOTU-6523 so this can ship before every env has
 * referral-service's admin endpoint deployed).
 *
 * The two backends take differently-shaped filters (monolith: `payFilter`;
 * referral-service: `status`/`program`/`isCancelled`), so `newSearchParams`
 * itself branches on the flag, not just the request built from it — the
 * off path is `origin/development`'s pre-PR code unchanged.
 */
export const fetchReferrals = (state) => async (dispatch, getState) => {
  const useReferralService = getShouldSourceReferralsFromReferralService();
  let sequence;

  try {
    let newSearchParams = {};
    if (state) {
      newSearchParams = useReferralService
        ? {
            page: state?.page || 1,
            limit: 15,
            status: state?.status && state.status !== 'all' ? state.status : '',
            referrerType:
              state?.referrerType && state.referrerType !== 'all' ? state.referrerType : '',
            program: state?.program && state.program !== 'all' ? state.program : '',
            isCancelled: state?.isCancelled && state.isCancelled !== 'all' ? state.isCancelled : '',
            searchText: state?.searchText || '',
          }
        : {
            page: state?.page || 1,
            limit: 15,
            payFilter: state?.payFilter || '',
            referrerType: state?.referrerType || '',
            searchText: state?.searchText || '',
          };
    }

    const { searchParams } = getState().referral;
    if (JSON.stringify(searchParams) === JSON.stringify(newSearchParams) && !state?.refresh) {
      return;
    }

    // Claim this fetch's sequence number now, at the point it actually goes
    // out — not earlier, so a dedup'd no-op above never advances it.
    requestSequence += 1;
    sequence = requestSequence;

    dispatch({
      type: REFERRAL_ACTIONS.GET_REFERRAL_REQUEST,
      payload: newSearchParams,
    });

    if (useReferralService) {
      const params = { page: newSearchParams.page, limit: newSearchParams.limit };
      if (newSearchParams.searchText) params.search = newSearchParams.searchText;
      if (newSearchParams.status) params.status = newSearchParams.status;
      if (newSearchParams.referrerType) {
        params.referrerType =
          REFERRER_TYPE_TO_PARAM[newSearchParams.referrerType] ?? newSearchParams.referrerType;
      }
      if (newSearchParams.program) params.program = newSearchParams.program;
      if (newSearchParams.isCancelled) params.isCancelled = newSearchParams.isCancelled;

      const response = await tempMee.get(REFERRAL_LIST_ENDPOINT, {
        params,
      });

      // A newer fetchReferrals call has gone out since this one was issued
      // (a later filter change, or the flag resolving) — that one owns the
      // store now, so this stale response is dropped rather than applied.
      if (sequence !== requestSequence) return;

      dispatch({
        type: REFERRAL_ACTIONS.GET_REFERRAL_SUCCESS,
        payload: {
          data: mapReferrals(response.data?.referrals),
          total: response.data?.total,
          source: 'referral-service',
        },
      });
    } else {
      const response = await tempMee.get(REFERRAL_LIST_ENDPOINT_MONOLITH, {
        params: newSearchParams,
      });

      if (sequence !== requestSequence) return;

      dispatch({
        type: REFERRAL_ACTIONS.GET_REFERRAL_SUCCESS,
        payload: { ...response.data, source: 'monolith' },
      });
    }
  } catch (err) {
    // Same staleness guard on the failure path: an older request's error
    // must not clobber a newer request's already-applied (or in-flight)
    // result.
    if (sequence !== undefined && sequence !== requestSequence) return;
    dispatch({ type: REFERRAL_ACTIONS.GET_REFERRAL_FAILURE, payload: err });
  }
};

// The monolith owns cancel either way — referral-service does not own
// writes yet (GOTU-7245). With the flag on, it is offered only for legacy
// office->office rows, keyed by the migrated recipient's monolith id
// (`migratedFrom`); with the flag off, callers pass the monolith row's own
// `id`. Either way this action just PATCHes whatever id it is given, so it
// needs no flag of its own.
export const cancelReferral = (id) => async (dispatch) => {
  try {
    dispatch({ type: REFERRAL_ACTIONS.CANCEL_REFERRAL_REQUEST });

    const response = await tempMee.patch(
      `/admin/referral/${id}`,
      { isCancelled: true },
    );

    dispatch({ type: REFERRAL_ACTIONS.CANCEL_REFERRAL_SUCCESS, payload: response.data });

    dispatch({
      type: SHOW_MESSAGE,
      payload: { message: 'The referral has been successfully cancelled.' },
    });
  } catch (err) {
    dispatch({ type: REFERRAL_ACTIONS.CANCEL_REFERRAL_FAILURE, payload: err });
  }
};
